import {
  FormEvent,
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { Eyebrow } from './primitives';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const REGISTER_URL = 'https://api.questbound.com/register-email';

const isValidEmail = (value: string) => EMAIL_REGEX.test(value.trim());

interface PublisherModalContextValue {
  open: () => void;
}

const PublisherModalContext = createContext<PublisherModalContextValue | null>(null);

export const useOpenPublisherModal = () => {
  const ctx = useContext(PublisherModalContext);
  if (!ctx) {
    throw new Error('useOpenPublisherModal must be used inside <PublisherModalProvider>');
  }
  return ctx.open;
};

export const PublisherModalProvider = ({ children }: { children: ReactNode }) => {
  const [isOpen, setIsOpen] = useState(false);

  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    window.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [isOpen, close]);

  return (
    <PublisherModalContext.Provider value={{ open }}>
      {children}
      {isOpen && <PublisherModal onClose={close} />}
    </PublisherModalContext.Provider>
  );
};

const PublisherModal = ({ onClose }: { onClose: () => void }) => {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    const trimmed = email.trim();
    if (!trimmed) {
      setError('Please enter an email address.');
      return;
    }
    if (!isValidEmail(trimmed)) {
      setError('Please enter a valid email address.');
      return;
    }
    setLoading(true);
    try {
      const tags = ['pp-apply'];
      const source = new URLSearchParams(window.location.search).get('source')?.trim().toLowerCase();
      if (source) tags.push(source);
      const res = await fetch(REGISTER_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: trimmed, tags }),
      });
      if (!res.ok) throw new Error(res.statusText || 'Request failed');
      setSubmitted(true);
      setEmail('');
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="publisher-modal-heading"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 100,
        background: 'rgba(10, 9, 8, 0.78)',
        backdropFilter: 'blur(6px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
      }}>
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          position: 'relative',
          width: '100%',
          maxWidth: 540,
          background: 'var(--qb-cream-50)',
          color: 'var(--qb-ink-800)',
          border: '3px solid var(--qb-ink-900)',
          boxShadow: 'var(--qb-shadow-stamp)',
          padding: '36px 36px 32px',
        }}>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          style={{
            position: 'absolute',
            top: 10,
            right: 10,
            width: 32,
            height: 32,
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            color: 'var(--qb-ink-800)',
            fontFamily: 'var(--qb-font-display)',
            fontSize: 22,
            lineHeight: 1,
          }}>
          ×
        </button>

        <Eyebrow color="var(--qb-orange-700)">PUBLISHER PROGRAM · FREE BETA</Eyebrow>
        <h2
          id="publisher-modal-heading"
          style={{
            fontFamily: 'var(--qb-font-display)',
            textTransform: 'uppercase',
            fontSize: 30,
            lineHeight: 1.05,
            letterSpacing: '-0.005em',
            margin: '12px 0 14px',
            color: 'var(--qb-ink-800)',
          }}>
          Apply to be a founding publisher.
        </h2>
        <p
          style={{
            fontFamily: 'var(--qb-font-body)',
            fontSize: 15,
            lineHeight: 1.6,
            color: '#42403e',
            margin: '0 0 22px',
          }}>
          The Publisher Program is in <strong>free beta</strong> while we work directly with our
          first creators. Drop your email and we'll reach out to get you set up with a storefront.
        </p>

        {submitted ? (
          <div
            style={{
              border: '2px solid var(--qb-ink-900)',
              padding: '16px 18px',
              fontFamily: 'var(--qb-font-body)',
              fontSize: 15,
              lineHeight: 1.5,
              background: '#f0e9d8',
            }}>
            <div
              style={{
                fontFamily: 'var(--qb-font-display)',
                fontSize: 11,
                letterSpacing: '0.16em',
                textTransform: 'uppercase',
                color: 'var(--qb-orange-700)',
                marginBottom: 6,
              }}>
              You're on the list
            </div>
            Thanks — we'll be in touch shortly with next steps.
          </div>
        ) : (
          <form onSubmit={handleSubmit} noValidate>
            <label
              htmlFor="publisher-modal-email"
              style={{
                display: 'block',
                fontFamily: 'var(--qb-font-display)',
                fontSize: 11,
                letterSpacing: '0.14em',
                textTransform: 'uppercase',
                color: '#6f6b67',
                marginBottom: 8,
              }}>
              Your email
            </label>
            <input
              id="publisher-modal-email"
              type="email"
              name="email"
              autoComplete="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={loading}
              aria-invalid={Boolean(error)}
              aria-describedby={error ? 'publisher-modal-error' : undefined}
              style={{
                width: '100%',
                padding: '12px 14px',
                fontFamily: 'var(--qb-font-body)',
                fontSize: 15,
                color: 'var(--qb-ink-800)',
                background: '#fff',
                border: '2px solid var(--qb-ink-900)',
                borderRadius: 0,
                outline: 'none',
                marginBottom: error ? 8 : 18,
                boxSizing: 'border-box',
              }}
            />
            {error && (
              <p
                id="publisher-modal-error"
                role="alert"
                style={{
                  fontFamily: 'var(--qb-font-body)',
                  fontSize: 13,
                  color: 'var(--qb-orange-700)',
                  margin: '0 0 14px',
                }}>
                {error}
              </p>
            )}
            <div style={{ display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap' }}>
              <button
                type="submit"
                disabled={loading}
                style={{
                  background: 'var(--qb-orange-500)',
                  color: '#fff',
                  border: '2px solid var(--qb-ink-900)',
                  boxShadow: '5px 5px 0 var(--qb-ink-900)',
                  padding: '14px 26px',
                  fontFamily: 'var(--qb-font-display)',
                  fontSize: 13,
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                  fontWeight: 700,
                  borderRadius: 4,
                  cursor: loading ? 'progress' : 'pointer',
                  opacity: loading ? 0.7 : 1,
                  transition:
                    'transform 120ms var(--qb-ease-standard), box-shadow 120ms var(--qb-ease-standard)',
                }}>
                {loading ? 'Submitting…' : 'Apply'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};
